/**
 * Water surfaces: the look a water plane resolves to, its presets, and which rendering tier a
 * device gets. Pure functions (no PlayCanvas), shared by the runtime (waterSystem.ts) and the
 * editor panel, and unit tested.
 *
 * BACK-COMPAT. Water planes saved before the v2 shader only carry color, deepColor,
 * waveStrength, waveSpeed, waveScale, waveDirection, stillWater, reflectivity and footprint.
 * They resolve with LEGACY_LOOK for every newer field: moderately murky water (so a plane laid
 * over a broken pool in a scan still hides most of what is under it), the old colours as the
 * shallow / deep tints, and reflectivity 0.5 meaning "physically correct Fresnel" as before.
 *
 * A plane may name a `preset`: fields it leaves out then come from that preset rather than the
 * legacy defaults, so an API user can write `{ preset: 'ocean' }` and get the whole look.
 * Explicit fields always win.
 */

export type WaterPresetId = 'pool' | 'lake' | 'ocean' | 'stylized';
export type WaterShapeKind = 'rectangle' | 'ellipse' | 'outline';
export type WaterReflectionMode = 'planar' | 'sky';

/** Every look parameter of a water surface, resolved (no undefined). */
export interface WaterLook {
    /** Shallow-water tint and colour of light scattered near the surface (#rrggbb). */
    color: string;
    /** Colour of deep water, where everything under the surface has been absorbed (#rrggbb). */
    deepColor: string;
    /** 0-1: how far you can see into the water, relative to the water's size. */
    clarity: number;
    /** 0-1: Fresnel reflection strength; 0.5 is physically based, 1 a mirror-like sheen. */
    reflectivity: number;
    /** Planar reflection of the scene (desktop) or sky only. */
    reflections: WaterReflectionMode;
    /** 0-1: how strongly the waves bend what is seen under the surface. */
    refraction: number;
    /** 0-1: dancing light pattern on what is under the water. */
    caustics: number;
    /** 0-1: foam where the water meets the shore and on wave crests. */
    foam: number;
    /** 0-2: strength of the sun highlight. */
    sunGlint: number;
    /** 0-1: how softly the water fades out where it meets the shore. */
    shoreSoftness: number;
    /** 0-2: wave normal strength. */
    waveStrength: number;
    /** 0-3: animation speed. */
    waveSpeed: number;
    /** Wave frequency; smaller = larger ripples. */
    waveScale: number;
    /** Main wave direction, degrees. */
    waveDirection: number;
    /** No wave motion at all (a still pond). */
    stillWater: boolean;
    /** Toon shading: banded light, hard highlights, crisp foam lines. */
    stylized: boolean;
    /** Sun direction used when the scene has no directional light: compass heading and height, degrees. */
    sunAzimuth: number;
    sunElevation: number;
}

/** The fields of a stored water plane this module reads (WaterPlaneData / the editor's WaterPlane). */
export interface WaterLookInput {
    preset?: string;
    color?: string;
    deepColor?: string;
    clarity?: number;
    reflectivity?: number;
    reflections?: string;
    refraction?: number;
    caustics?: number;
    foam?: number;
    sunGlint?: number;
    shoreSoftness?: number;
    waveStrength?: number;
    waveSpeed?: number;
    waveScale?: number;
    waveDirection?: number;
    stillWater?: boolean;
    stylized?: boolean;
    sunAzimuth?: number;
    sunElevation?: number;
}

/** What water planes saved before the v2 shader get for every field they don't have. */
export const LEGACY_LOOK: WaterLook = {
    color: '#1e6b8a',
    deepColor: '#03283a',
    clarity: 0.35,
    reflectivity: 0.5,
    reflections: 'planar',
    refraction: 0.5,
    caustics: 0.2,
    foam: 0.15,
    sunGlint: 1,
    shoreSoftness: 0.4,
    waveStrength: 1,
    waveSpeed: 1,
    waveScale: 1,
    waveDirection: 0,
    stillWater: false,
    stylized: false,
    sunAzimuth: 35,
    sunElevation: 50,
};

export interface WaterPreset {
    id: WaterPresetId;
    label: string;
    description: string;
    look: WaterLook;
}

/** The presets the editor offers; each sets every look parameter (not the shape). */
export const WATER_PRESETS: readonly WaterPreset[] = [
    {
        id: 'pool',
        label: 'Pool',
        description: 'Clear turquoise water, small ripples, light patterns on the floor.',
        look: {
            color: '#36c5d6',
            deepColor: '#0f5f8c',
            clarity: 0.85,
            reflectivity: 0.5,
            reflections: 'planar',
            refraction: 0.6,
            caustics: 0.65,
            foam: 0,
            sunGlint: 1.1,
            shoreSoftness: 0.25,
            waveStrength: 0.45,
            waveSpeed: 0.7,
            waveScale: 1.6,
            waveDirection: 30,
            stillWater: false,
            stylized: false,
            sunAzimuth: 35,
            sunElevation: 55,
        },
    },
    {
        id: 'lake',
        label: 'Lake',
        description: 'Darker green-blue water with a calm surface and soft reflections.',
        look: {
            color: '#5a8a72',
            deepColor: '#17443a',
            clarity: 0.35,
            reflectivity: 0.7,
            reflections: 'planar',
            refraction: 0.4,
            caustics: 0.15,
            foam: 0.1,
            sunGlint: 0.9,
            shoreSoftness: 0.45,
            waveStrength: 0.55,
            waveSpeed: 0.5,
            waveScale: 0.9,
            waveDirection: 20,
            stillWater: false,
            stylized: false,
            sunAzimuth: 35,
            sunElevation: 40,
        },
    },
    {
        id: 'ocean',
        label: 'Ocean',
        description: 'Deep blue swell, strong highlights and foam along the shore.',
        look: {
            color: '#2fa3b3',
            deepColor: '#0a3556',
            clarity: 0.3,
            reflectivity: 0.65,
            reflections: 'planar',
            refraction: 0.5,
            caustics: 0.1,
            foam: 0.65,
            sunGlint: 1.4,
            shoreSoftness: 0.6,
            waveStrength: 1.25,
            waveSpeed: 1,
            waveScale: 0.55,
            waveDirection: 0,
            stillWater: false,
            stylized: false,
            sunAzimuth: 35,
            sunElevation: 35,
        },
    },
    {
        id: 'stylized',
        label: 'Stylised',
        description: 'Cartoon water: bright colour bands, hard highlights and crisp foam lines.',
        look: {
            color: '#4fe3f5',
            deepColor: '#1846c9',
            clarity: 0.55,
            reflectivity: 0.4,
            reflections: 'sky',
            refraction: 0.3,
            caustics: 0.6,
            foam: 0.85,
            sunGlint: 1.2,
            shoreSoftness: 0.3,
            waveStrength: 0.8,
            waveSpeed: 0.8,
            waveScale: 1.1,
            waveDirection: 45,
            stillWater: false,
            stylized: true,
            sunAzimuth: 35,
            sunElevation: 50,
        },
    },
];

/** Look of a preset id, or null for an unknown one. */
export function waterPresetLook(id: string | undefined): WaterLook | null {
    const preset = WATER_PRESETS.find((p) => p.id === id);
    return preset ? { ...preset.look } : null;
}

const HEX = /^#[0-9a-f]{6}$/i;
const num = (value: unknown, fallback: number, min: number, max: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;

/**
 * The look a stored water plane renders with: its own fields, then its preset's, then the legacy
 * defaults (so water saved before the v2 shader looks sensible), every value clamped.
 */
export function resolveWaterLook(data: WaterLookInput | null | undefined): WaterLook {
    const d = data ?? {};
    const base = waterPresetLook(d.preset) ?? LEGACY_LOOK;
    return {
        color: typeof d.color === 'string' && HEX.test(d.color) ? d.color : base.color,
        deepColor: typeof d.deepColor === 'string' && HEX.test(d.deepColor) ? d.deepColor : base.deepColor,
        clarity: num(d.clarity, base.clarity, 0, 1),
        reflectivity: num(d.reflectivity, base.reflectivity, 0, 1),
        reflections: d.reflections === 'sky' || d.reflections === 'planar' ? d.reflections : base.reflections,
        refraction: num(d.refraction, base.refraction, 0, 1),
        caustics: num(d.caustics, base.caustics, 0, 1),
        foam: num(d.foam, base.foam, 0, 1),
        sunGlint: num(d.sunGlint, base.sunGlint, 0, 2),
        shoreSoftness: num(d.shoreSoftness, base.shoreSoftness, 0, 1),
        waveStrength: num(d.waveStrength, base.waveStrength, 0, 2),
        waveSpeed: num(d.waveSpeed, base.waveSpeed, 0, 3),
        waveScale: num(d.waveScale, base.waveScale, 0.05, 10),
        waveDirection: num(d.waveDirection, base.waveDirection, -3600, 3600),
        stillWater: typeof d.stillWater === 'boolean' ? d.stillWater : base.stillWater,
        stylized: typeof d.stylized === 'boolean' ? d.stylized : base.stylized,
        sunAzimuth: num(d.sunAzimuth, base.sunAzimuth, -3600, 3600),
        sunElevation: num(d.sunElevation, base.sunElevation, 1, 90),
    };
}

/** The fields a preset writes onto a stored plane (everything but the shape and transform). */
export function waterPresetPatch(id: WaterPresetId): WaterLook & { preset: WaterPresetId } {
    const look = waterPresetLook(id) ?? { ...LEGACY_LOOK };
    return { ...look, preset: id };
}

/** Which shape a stored plane has: its own `shape`, else an outline when it has a footprint. */
export function waterShapeKind(data: { shape?: string; footprint?: unknown[] | null } | null | undefined): WaterShapeKind {
    if (data?.shape === 'rectangle' || data?.shape === 'ellipse' || data?.shape === 'outline') return data.shape;
    return Array.isArray(data?.footprint) && data!.footprint!.length >= 3 ? 'outline' : 'rectangle';
}

// ── Rendering tiers ────────────────────────────────────────────────────────────────────────

/**
 * - 'high': desktop. Refraction from a copy of the frame (the splats seen through the water,
 *   bent by the waves), planar reflections of the scene, four layers of detail normals.
 * - 'low': phones, tablets, headsets and performance mode. No extra scene render or frame copy:
 *   the water alpha-blends over the splats (still see-through, absorbed with depth), reflects a
 *   sky gradient, two detail layers and two swell waves.
 * - 'opaque': while the splat depth pass can't run (WebXR sessions): drawn before the splats
 *   and occluded by them like the original water, surface shading only.
 */
export type WaterQualityTier = 'high' | 'low' | 'opaque';

export interface WaterQualityInputs {
    /** Phone / tablet / standalone headset GPU class (or the editor's mobile preview). */
    mobile: boolean;
    /** The viewer's performance mode (user toggle or forced by the creator). */
    performanceMode: boolean;
    /** An immersive XR session is running. */
    xr: boolean;
    /** The splat depth texture is available on this device (float or RGBA8 render target). */
    splatDepthAvailable?: boolean;
    /** Diagnostics override (`?waterQuality=high|low|opaque`). */
    forced?: WaterQualityTier | null;
}

export function selectWaterQuality(inputs: WaterQualityInputs): WaterQualityTier {
    if (inputs.forced === 'high' || inputs.forced === 'low' || inputs.forced === 'opaque') return inputs.forced;
    if (inputs.xr || inputs.splatDepthAvailable === false) return 'opaque';
    if (inputs.mobile || inputs.performanceMode) return 'low';
    return 'high';
}

/** True when a tier renders planar reflections for this look. */
export function usesPlanarReflection(tier: WaterQualityTier, look: Pick<WaterLook, 'reflections' | 'reflectivity'>): boolean {
    return tier === 'high' && look.reflections === 'planar' && look.reflectivity > 0;
}

/** True when a tier copies the frame for refraction. */
export function usesRefractionGrab(tier: WaterQualityTier): boolean {
    return tier === 'high';
}

// ── Shader parameter packing ───────────────────────────────────────────────────────────────

/** #rrggbb → linear RGB (sRGB decode), the space the water shader works in. */
export function hexToLinear(hex: string, fallback: [number, number, number] = [0, 0, 0]): [number, number, number] {
    if (!HEX.test(hex)) return fallback;
    const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    return c.map((v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4))) as [number, number, number];
}

/**
 * How far light travels into the water before it is ~95% absorbed, in scene units, from clarity
 * and the water's size (the diagonal of its outline): 2% of the size at clarity 0, the whole
 * size at clarity 1, exponential in between so the slider feels even.
 */
export function waterVisibilityDistance(clarity: number, extent: number): number {
    const size = Math.max(extent, 1e-3);
    const c = Math.min(1, Math.max(0, clarity));
    return size * 0.02 * Math.pow(50, c);
}

/** How far the water thins out toward the shore (scene units): the shader's uMetrics.w. */
export function waterShoreDistance(extent: number, shoreSoftness: number): number {
    return extent * 0.03 * shoreSoftness;
}

/**
 * Height above the surface (scene units) where a splat starts to hide the water in the shader's
 * coverage test (`tol` in waterShaders.ts: it hides it fully at twice this). The splat draw's
 * split at the water (waterSplatSplit.ts) puts splats higher than this in front of the water, so
 * the scan's own noisy pond-surface splats stay under it.
 */
export function waterCoverageTolerance(extent: number, shoreSoftness: number): number {
    return extent * 0.004 + waterShoreDistance(extent, shoreSoftness) * 0.25 + 1e-4;
}

/** Direction towards the sun (y up) from a compass heading and height in degrees. */
export function sunDirection(azimuthDeg: number, elevationDeg: number): [number, number, number] {
    const az = (azimuthDeg * Math.PI) / 180;
    const el = (Math.min(90, Math.max(1, elevationDeg)) * Math.PI) / 180;
    return [Math.cos(el) * Math.sin(az), Math.sin(el), Math.cos(el) * Math.cos(az)];
}
